import { type ReactElement, type ReactNode } from 'react'

import { useTranslate } from '@orus.eu/pharaoh'
import { LoadingRegion } from '@orus.eu/pharaoh-next/components/ui/loading-region'
import { Skeleton } from '@orus.eu/pharaoh-next/components/ui/skeleton'
import { View } from '@orus.eu/pharaoh-next/components/ui/view'

/**
 * Pending state shared by both CCI certificate generators: the `View` frame, the intro paragraph and
 * the download action. Each screen fills `children` with its own rows, so its skeleton stays a
 * readable mirror of its form.
 */
export function CertificateGeneratorSkeleton({ children }: { children: ReactNode }): ReactElement {
  const translate = useTranslate()

  return (
    <LoadingRegion label={translate('loading')}>
      <View.Root>
        <View.Header>
          <Skeleton variant="text" width={280} height={36} />
        </View.Header>

        <View.Body>
          <div className="flex flex-col gap-20">
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="70%" />
          </div>

          {/* Deliberately unwrapped: the pages put only their form rows inside a `Section.Root` and
              leave the other blocks as direct `View.Body` children, so the 48px `gap-section` between
              blocks has to come from `View.Body` here too. Wrapping everything in one `Section.Root`
              made the pending state 16px where the resolved one is 48px. */}
          {children}
        </View.Body>

        <View.Footer>
          <Skeleton variant="button" width={150} />
        </View.Footer>
      </View.Root>
    </LoadingRegion>
  )
}
